import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Icon from "../../components/Icon";
import SearchableSelect from "../../components/SearchableSelect";
import LoadingBlock from "../../components/LoadingBlock";
import { prefillInvoice, createInvoice, getInvoice, updateInvoice, getNextInvoiceNumber } from "../../api/invoiceService";
import { listFirmProfiles, getFirmProfile } from "../../api/firmProfileService";
import { useNamespace } from "../../hooks/useNamespace";
import { showApiError, showToast } from "../../utils/toast";
import { formatINRExact } from "../../utils/formatters";

// Prefilled invoice form. Two entry points:
//   /admin/invoices/new?payment_instance_id=X — create mode; loads via prefill
//   /admin/invoices/:id/edit                   — edit mode; loads via getInvoice
// Everything is editable. On save, server re-runs the tax math and either
// inserts or updates the snapshot, then navigates to the print preview.
export default function InvoiceFormPage() {
  const [params] = useSearchParams();
  const paymentInstanceId = params.get("payment_instance_id");
  const { id: editId } = useParams();
  const editing = !!editId;
  const navigate = useNavigate();
  const ns = useNamespace();

  const [firmProfiles, setFirmProfiles] = useState([]);
  const firmOptions = useMemo(
    () => firmProfiles.map((p) => ({ id: p.id, label: p.firm_name })),
    [firmProfiles],
  );
  const [firmId, setFirmId] = useState("");
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load firm profiles once — the dropdown only sets firm_profile_id in edit
  // mode (the seller snapshot is frozen onto the invoice already).
  useEffect(() => {
    listFirmProfiles()
      .then((res) => setFirmProfiles(res?.data || []))
      .catch(() => setFirmProfiles([]));
  }, []);

  // Initial load — mount-only. In edit mode, getInvoice; in create mode,
  // prefill with whatever firm we have (none = backend picks the first).
  useEffect(() => {
    setLoading(true);
    if (editing) {
      getInvoice(editId)
        .then((res) => {
          const inv = res?.data;
          if (inv) {
            setForm(inv);
            if (inv.firm_profile_id) setFirmId(String(inv.firm_profile_id));
          }
        })
        .catch((err) => showApiError(err, "Failed to load invoice"))
        .finally(() => setLoading(false));
      return;
    }
    if (!paymentInstanceId) {
      setLoading(false);
      return;
    }
    prefillInvoice({ payment_instance_id: paymentInstanceId })
      .then((res) => {
        setForm(res?.data);
        if (res?.data?.firm_profile_id) setFirmId(String(res.data.firm_profile_id));
      })
      .catch((err) => showApiError(err, "Failed to load invoice prefill"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, editId, paymentInstanceId]);

  // Firm-dropdown change. Both modes: fetch the chosen FirmProfile and
  // patch *only* the seller snapshot fields + re-derive inter-state.
  // Keeps the user's existing line items, buyer details, invoice_no, and
  // dates intact — same behaviour in create and edit so the dropdown is
  // never silently inert.
  useEffect(() => {
    if (!form || !firmId) return;
    if (String(form.firm_profile_id ?? "") === String(firmId)) return;

    getFirmProfile(firmId)
      .then((res) => {
        const firm = res?.data;
        if (!firm) return;
        setForm((f) => ({
          ...f,
          firm_profile_id: firm.id,
          firm_name: firm.firm_name ?? "",
          firm_address: firm.address ?? "",
          firm_gstin: firm.gstin ?? "",
          firm_pan: firm.pan ?? "",
          firm_state_name: firm.state_name ?? "",
          firm_state_code: firm.state_code ?? "",
          firm_bank_name: firm.bank_name ?? "",
          firm_bank_account_no: firm.bank_account_no ?? "",
          firm_bank_branch: firm.bank_branch ?? "",
          firm_bank_ifsc: firm.bank_ifsc ?? "",
          signatory_name: firm.signatory_name ?? "",
          // Keep the user's edited declaration if they typed one; only
          // pull from the firm when the current value is blank.
          declaration_text: f.declaration_text || firm.declaration_text || "",
          // Re-derive inter-state from the *new* firm vs current buyer.
          // If either state code is missing, leave whatever's there.
          inter_state: (firm.state_code && f.buyer_state_code)
            ? firm.state_code !== f.buyer_state_code
            : f.inter_state,
          // Push the new firm's default HSN onto every taxable line so a
          // firm switch refreshes the SAC code that prints on the invoice.
          // apply_gst=false lines (expense pass-throughs) stay HSN-null —
          // they're not GST-classified.
          lines: (f.lines || []).map((l) =>
            (l.apply_gst !== false)
              ? { ...l, hsn_code: firm.default_hsn ?? l.hsn_code ?? "" }
              : l
          ),
        }));
        // Re-suggest the invoice number for the new (firm, treatment)
        // bucket — only in create mode; edit keeps the admin's number.
        if (!editing && form?.gst_treatment) {
          getNextInvoiceNumber({ firm_profile_id: firm.id, gst_treatment: form.gst_treatment })
            .then((r) => {
              const n = r?.data?.invoice_no;
              if (Number.isInteger(n)) setForm((f) => ({ ...f, invoice_no: n }));
            })
            .catch(() => {/* leave invoice_no as-is on failure */});
        }
      })
      .catch((err) => showApiError(err, "Failed to switch firm"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLine = (i, patch) => setForm((f) => ({
    ...f,
    lines: f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)),
  }));
  const addLine = () => setForm((f) => ({
    ...f,
    lines: [...f.lines, {
      description: "",
      hsn_code: f.lines[0]?.hsn_code || "",
      amount: 0,
      // New lines opt into the invoice's GST mode by default; toggle off
      // per line for reimbursable expenses.
      apply_gst: true,
    }],
  }));
  const removeLine = (i) => setForm((f) => ({
    ...f,
    lines: f.lines.length > 1 ? f.lines.filter((_, idx) => idx !== i) : f.lines,
  }));

  // Live preview of the totals — server is authoritative on save, but
  // showing the math as the user edits is the whole point of the form.
  const totals = useMemo(() => computeTotals(form), [form]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      const payload = { ...form };
      // Normalise the per-line payload to what the backend expects. Qty/rate
      // are dropped from the UI but the columns still exist on invoice_lines
      // — send them as null so prefilled values can't accidentally persist.
      payload.lines = (payload.lines || []).map((l) => ({
        ...l,
        quantity: null,
        rate: null,
        amount: Number(l.amount || 0),
        apply_gst: l.apply_gst !== false, // default true
      }));
      const res = editing
        ? await updateInvoice(editId, payload)
        : await createInvoice(payload);
      const id = res?.data?.id || editId;
      showToast("success", editing ? "Invoice updated" : "Invoice generated");
      if (id) navigate(`${ns}/invoices/${id}`);
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err, editing ? "Failed to update invoice" : "Failed to generate invoice");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1 className="page-title">{editing ? "Edit Invoice" : "Generate Invoice"}</h1>
            <div className="page-sub">{editing ? "Loading invoice…" : "Prefilling from payment instance…"}</div>
          </div>
        </div>
        <div className="card"><div className="card-body"><LoadingBlock rows={6} /></div></div>
      </>
    );
  }

  const showCgstSgst = form.gst_treatment === "gst" && !form.inter_state;
  const showIgst = form.gst_treatment === "gst" && form.inter_state;

  return (
    <>
      <div className="page-head">
        <div>
          <Link to={editing ? `${ns}/invoices/${editId}` : `${ns}/payments`} className="t-muted text-xs">
            <Icon name="arrow-left" size={11} /> {editing ? "Back to invoice" : "Back to Payments"}
          </Link>
          <h1 className="page-title">{editing ? `Edit Invoice ${form.invoice_no}` : "Generate Invoice"}</h1>
          <div className="page-sub">
            {editing
              ? "Server re-runs the tax math on save — all snapshot fields stay frozen as edited."
              : "Prefilled from the bill — everything below is editable before you save."}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            <Icon name="check" size={14} /> {saving ? "Saving…" : editing ? "Save changes" : "Save & preview"}
          </button>
        </div>
      </div>

      {/* Firm picker */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body">
          <div className="row gap-md" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="field" style={{ flex: 1, minWidth: 240, marginBottom: 0 }}>
              <label className="field-label">Firm (seller)</label>
              {/* setFirmId is what drives the seller-snapshot refetch — keep
                  routing through it rather than setting state inline. */}
              <SearchableSelect
                value={firmId}
                onChange={(v) => setFirmId(v ?? "")}
                options={firmOptions}
                placeholder="— pick a firm —"
                searchPlaceholder="Search firms…"
                width="100%"
              />
              <div className="field-hint">
                Switching the firm refetches the seller snapshot and recalculates intra/inter-state.
              </div>
            </div>
            <div className="field" style={{ width: 180, marginBottom: 0 }}>
              <label className="field-label">Invoice no.</label>
              <input
                type="number"
                className="input t-mono"
                value={form.invoice_no}
                onChange={(e) => set({ invoice_no: e.target.value === "" ? "" : Number(e.target.value) })}
              />
              {errors.invoice_no?.[0] && <div className="field-error">{errors.invoice_no[0]}</div>}
            </div>
            <div className="field" style={{ width: 180, marginBottom: 0 }}>
              <label className="field-label">Invoice date</label>
              <input
                type="date"
                className="input"
                value={form.invoice_date || ""}
                onChange={(e) => set({ invoice_date: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Seller + Buyer snapshot (collapsible-ish; printed below visible) */}
      <div className="row gap-md" style={{ marginBottom: 12, flexWrap: "wrap" }}>
        <div className="card" style={{ flex: 1, minWidth: "min(340px, 100%)" }}>
          <div className="card-body">
            <div className="t-muted text-xs" style={{ textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
              Seller (firm) snapshot
            </div>
            <SnapshotGrid
              fields={[
                ["firm_name", "Firm name"],
                ["firm_gstin", "GSTIN", true],
                ["firm_pan", "PAN", true],
                ["firm_state_name", "State"],
                ["firm_state_code", "State code", true],
              ]}
              form={form}
              set={set}
              errors={errors}
            />
            <div className="field" style={{ marginTop: 8 }}>
              <label className="field-label">Address</label>
              <textarea
                className="textarea"
                rows={2}
                value={form.firm_address || ""}
                onChange={(e) => set({ firm_address: e.target.value })}
              />
            </div>
          </div>
        </div>
        <div className="card" style={{ flex: 1, minWidth: "min(340px, 100%)" }}>
          <div className="card-body">
            <div className="t-muted text-xs" style={{ textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
              Buyer (bill to) snapshot
            </div>
            <SnapshotGrid
              fields={[
                ["buyer_name", "Buyer name"],
                ["buyer_client_id", "Client ID", true],
                ["buyer_phone", "Mobile", true],
                ["buyer_gstin", "GSTIN", true],
                ["buyer_state_name", "State"],
                ["buyer_state_code", "State code", true],
              ]}
              form={form}
              set={set}
              errors={errors}
            />
            <div className="field" style={{ marginTop: 8 }}>
              <label className="field-label">Address</label>
              <textarea
                className="textarea"
                rows={2}
                value={form.buyer_address || ""}
                onChange={(e) => set({ buyer_address: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tax mode + rates */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body">
          <div className="row gap-md" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="field" style={{ width: 200, marginBottom: 0 }}>
              <label className="field-label">GST treatment</label>
              <select
                className="input"
                value={form.gst_treatment}
                onChange={(e) => {
                  const next = e.target.value;
                  set({ gst_treatment: next });
                  // Invoice numbers are scoped per (firm, treatment) — so
                  // flipping treatment in create mode should re-suggest the
                  // next number for the new series. Edit mode keeps the
                  // admin-chosen number untouched.
                  if (!editing && firmId && (next === "gst" || next === "no_gst")) {
                    getNextInvoiceNumber({ firm_profile_id: firmId, gst_treatment: next })
                      .then((res) => {
                        const n = res?.data?.invoice_no;
                        if (Number.isInteger(n)) set({ invoice_no: n });
                      })
                      .catch(() => {/* leave invoice_no as-is on failure */});
                  }
                }}
              >
                <option value="gst">GST</option>
                <option value="no_gst">No GST</option>
              </select>
            </div>
            <div className="field" style={{ width: 200, marginBottom: 0 }}>
              <label className="field-label">Place of supply</label>
              <select
                className="input"
                value={form.inter_state ? "inter" : "intra"}
                onChange={(e) => set({ inter_state: e.target.value === "inter" })}
              >
                <option value="intra">Intra-state (CGST+SGST)</option>
                <option value="inter">Inter-state (IGST)</option>
              </select>
              <div className="field-hint">
                Auto-derived from firm vs buyer state codes — flip manually if needed.
              </div>
            </div>
            {showCgstSgst && (
              <>
                <div className="field" style={{ width: 110, marginBottom: 0 }}>
                  <label className="field-label">CGST %</label>
                  <input
                    type="number" step="0.01" className="input t-mono"
                    value={form.cgst_rate}
                    onChange={(e) => set({ cgst_rate: Number(e.target.value || 0) })}
                  />
                </div>
                <div className="field" style={{ width: 110, marginBottom: 0 }}>
                  <label className="field-label">SGST %</label>
                  <input
                    type="number" step="0.01" className="input t-mono"
                    value={form.sgst_rate}
                    onChange={(e) => set({ sgst_rate: Number(e.target.value || 0) })}
                  />
                </div>
              </>
            )}
            {showIgst && (
              <div className="field" style={{ width: 110, marginBottom: 0 }}>
                <label className="field-label">IGST %</label>
                <input
                  type="number" step="0.01" className="input t-mono"
                  value={form.igst_rate}
                  onChange={(e) => set({ igst_rate: Number(e.target.value || 0) })}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body">
          <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
            <div className="t-muted text-xs" style={{ textTransform: "uppercase", letterSpacing: ".06em" }}>
              Line items {form.gst_treatment === "gst" && <em>(amounts are GST-inclusive)</em>}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={addLine}>
              <Icon name="plus" size={13} /> Add line
            </button>
          </div>
          {/* table-wrap: fixed column widths (~510px) overflow the padded
              card on phones — scroll the grid instead of squeezing inputs. */}
          <div className="table-wrap" style={{ marginTop: 4 }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Description</th>
                {/* HSN/SAC is a GST-classification code; non-GST invoices
                    don't need it, so the column is hidden in that mode. */}
                {form.gst_treatment === "gst" && <th style={{ width: 110 }}>HSN/SAC</th>}
                <th style={{ width: 130 }}>Amount</th>
                {/* Taxable + GST columns matter only when the invoice is GST. */}
                {form.gst_treatment === "gst" && <th style={{ width: 130 }}>Taxable</th>}
                {form.gst_treatment === "gst" && <th style={{ width: 60 }} title="Apply GST on this line">GST</th>}
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((l, i) => {
                const applyGst = l.apply_gst !== false; // default true
                const lineTaxable = computeLineTaxable(form, l);
                return (
                <tr key={i}>
                  <td className="t-num t-muted">{i + 1}</td>
                  <td>
                    <input
                      className="input"
                      value={l.description}
                      onChange={(e) => setLine(i, { description: e.target.value })}
                      placeholder="e.g. Monthly Consultation Fees"
                    />
                  </td>
                  {form.gst_treatment === "gst" && (
                    <td>
                      <input
                        className="input t-mono"
                        value={l.hsn_code || ""}
                        // HSN doesn't apply to non-GST (expense) lines —
                        // disable + clear it visually when this line opts out.
                        onChange={(e) => setLine(i, { hsn_code: e.target.value })}
                        disabled={!applyGst}
                        placeholder={applyGst ? "998224" : "—"}
                      />
                    </td>
                  )}
                  <td>
                    <input
                      type="number" step="0.01" className="input t-mono"
                      value={l.amount ?? 0}
                      onChange={(e) => setLine(i, { amount: e.target.value })}
                    />
                  </td>
                  {form.gst_treatment === "gst" && (
                    <td className="t-num t-mono">{formatINRExact(lineTaxable)}</td>
                  )}
                  {form.gst_treatment === "gst" && (
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={applyGst}
                        onChange={(e) => {
                          const next = e.target.checked;
                          // Toggling GST off drops the HSN — it's a GST
                          // classification code and shouldn't sit on
                          // pass-through expense lines (the server enforces
                          // this too in InvoiceCalculator).
                          setLine(i, next ? { apply_gst: true } : { apply_gst: false, hsn_code: null });
                        }}
                        title={applyGst
                          ? "GST is applied on this line (back-calculated from Amount)"
                          : "GST is NOT applied — Amount is treated as taxable value"}
                      />
                    </td>
                  )}
                  <td>
                    <button
                      className="icon-btn"
                      style={{ color: "var(--danger)" }}
                      disabled={form.lines.length <= 1}
                      onClick={() => removeLine(i)}
                      title="Remove line"
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {errors["lines"]?.[0] && <div className="field-error">{errors["lines"][0]}</div>}
        </div>
      </div>

      {/* Live totals preview */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body">
          <div className="t-muted text-xs" style={{ textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
            Preview (server is authoritative on save)
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <Total label="Taxable value" value={totals.taxable_total} />
            {showCgstSgst && <Total label="CGST" value={totals.cgst_amount} />}
            {showCgstSgst && <Total label="SGST" value={totals.sgst_amount} />}
            {showIgst && <Total label="IGST" value={totals.igst_amount} />}
            <Total label="Grand total" value={totals.grand_total} bold />
          </div>
        </div>
      </div>

      {/* Signatory + declaration */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body col gap-md">
          <div className="field">
            <label className="field-label">Authorised signatory name</label>
            <input
              className="input"
              value={form.signatory_name || ""}
              onChange={(e) => set({ signatory_name: e.target.value })}
            />
          </div>
          <div className="field">
            <label className="field-label">Declaration text</label>
            <textarea
              className="textarea"
              rows={2}
              value={form.declaration_text || ""}
              onChange={(e) => set({ declaration_text: e.target.value })}
            />
          </div>
        </div>
      </div>
    </>
  );
}

function SnapshotGrid({ fields, form, set, errors }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
      {fields.map(([key, label, mono]) => (
        <div className="field" key={key} style={{ marginBottom: 0 }}>
          <label className="field-label">{label}</label>
          <input
            className={`input ${mono ? "t-mono" : ""}`}
            value={form[key] || ""}
            onChange={(e) => set({ [key]: e.target.value })}
          />
          {errors[key]?.[0] && <div className="field-error">{errors[key][0]}</div>}
        </div>
      ))}
    </div>
  );
}

function Total({ label, value, bold = false }) {
  return (
    <div>
      <div className="t-muted text-xs">{label}</div>
      <div className={`t-mono ${bold ? "t-primary" : ""}`} style={{ fontWeight: bold ? 700 : 500, fontSize: bold ? 18 : 15 }}>
        {formatINRExact(value)}
      </div>
    </div>
  );
}

// Mirror of `app\Services\InvoiceCalculator::compute` — preview only;
// server is authoritative on save.
function computeTotals(form) {
  const acc = { taxable_total: 0, cgst_amount: 0, sgst_amount: 0, igst_amount: 0, grand_total: 0 };
  if (!form) return acc;

  for (const line of form.lines || []) {
    const amount = Number(line.amount || 0);
    const applyGst = line.apply_gst !== false;
    let taxable = amount, cgst = 0, sgst = 0, igst = 0;

    // Only lines that opt into GST on a GST invoice get taxed; expense /
    // pass-through lines fall through with taxable = amount, tax columns 0.
    if (form.gst_treatment === "gst" && applyGst) {
      if (form.inter_state) {
        const r = Number(form.igst_rate || 0);
        taxable = r > 0 ? (amount * 100) / (100 + r) : amount;
        igst = (taxable * r) / 100;
      } else {
        const c = Number(form.cgst_rate || 0);
        const s = Number(form.sgst_rate || 0);
        const t = c + s;
        taxable = t > 0 ? (amount * 100) / (100 + t) : amount;
        cgst = (taxable * c) / 100;
        sgst = (taxable * s) / 100;
      }
    }

    acc.taxable_total += round2(taxable);
    acc.cgst_amount += round2(cgst);
    acc.sgst_amount += round2(sgst);
    acc.igst_amount += round2(igst);
    acc.grand_total += round2(amount);
  }
  for (const k of Object.keys(acc)) acc[k] = round2(acc[k]);
  return acc;
}

// Single-line taxable preview for the table's Taxable column.
function computeLineTaxable(form, line) {
  const amount = Number(line.amount || 0);
  const applyGst = line.apply_gst !== false;
  if (form.gst_treatment !== "gst" || !applyGst) return round2(amount);
  if (form.inter_state) {
    const r = Number(form.igst_rate || 0);
    return round2(r > 0 ? (amount * 100) / (100 + r) : amount);
  }
  const c = Number(form.cgst_rate || 0);
  const s = Number(form.sgst_rate || 0);
  const t = c + s;
  return round2(t > 0 ? (amount * 100) / (100 + t) : amount);
}

function round2(n) { return Math.round(n * 100) / 100; }
